/**
 * `useSkeletonCrossFade` — a loading skeleton cross-fades into the content
 * that replaces it, instead of snapping (#2172, the laptop motion pass;
 * first built on the 4 Oct UI-motion branch).
 *
 *   const fade = useSkeletonCrossFade(query.isLoading);
 *   if (query.isLoading) return <div ref={fade.ref}>…skeletons…</div>;
 *   return <div ref={fade.ref} className={fade.contentClassName}>…</div>;
 *
 * The same `ref` goes on the skeleton's wrapper and on the content's. When
 * loading ends:
 *   - the content mounts with `motion-content-in` (it eases up into
 *     place, styles/motion-shell.css) — only when a skeleton showed first,
 *     so a page that had its data cached appears as it always did;
 *   - a copy of the departing skeleton is laid over the content's slot
 *     (its own height, `pointer-events: none`, test ids stripped) and
 *     fades out over the same 320 ms (`motion`, opacity only), then is
 *     removed. React never sees the copy, so nothing in the tree changes.
 *
 * The copy is placed in DOCUMENT coordinates (it scrolls with the page),
 * so it is only right for in-flow call sites — never inside a fixed or
 * sticky container (a drawer, a sheet), where it would sit in the wrong
 * place once anything scrolls.
 *
 * `{ withinOffsetParent: true }` (#2172 M0) lays the copy inside the
 * content's positioned ancestor instead, in that box's own coordinates, so
 * it scrolls and moves WITH the content: for content inside its own scroll
 * container on a layer that may still be sliding in — the phone app's
 * screens, where the document never scrolls and a pushed screen can get its
 * data mid-push. The ancestor must be positioned (the phone app's scroll
 * container is); without one the copy falls back to the document.
 *
 * The copy only runs where JS motion may (`jsMotionEnabled`: not under
 * `prefers-reduced-motion`, not under test) and with `element.animate`;
 * the CSS ease-in collapses to its end state under reduced motion on its
 * own.
 */
import { animate } from "motion/mini";
import { useCallback, useLayoutEffect, useRef, useState } from "react";

import { DUR_SLOW_MS, EASE_OUT_SOFT_POINTS, jsMotionEnabled, seconds } from "./motionTokens";

export const SKELETON_FADE_MS = DUR_SLOW_MS;

export interface SkeletonCrossFade {
  /** Attach to the skeleton's wrapper AND to the content's wrapper. */
  ref: (el: HTMLElement | null) => void;
  /** `motion-content-in` on the content's first render after a skeleton. */
  contentClassName: string | undefined;
}

export interface SkeletonCrossFadeOptions {
  /** Lay the departing copy in the content's positioned ancestor (see above). */
  withinOffsetParent?: boolean;
}

export function useSkeletonCrossFade(
  loading: boolean,
  { withinOffsetParent = false }: SkeletonCrossFadeOptions = {},
): SkeletonCrossFade {
  const [enabled] = useState(jsMotionEnabled);
  const nodeRef = useRef<HTMLElement | null>(null);
  // The skeleton on screen during the last loading render, and its height.
  const skeletonRef = useRef<{ node: HTMLElement; height: number } | null>(null);
  const sawLoadingRef = useRef(false);
  if (loading) sawLoadingRef.current = true;

  const ref = useCallback((el: HTMLElement | null) => {
    nodeRef.current = el;
  }, []);

  useLayoutEffect(() => {
    const el = nodeRef.current;
    if (loading) {
      skeletonRef.current = el ? { node: el, height: el.offsetHeight } : null;
      return;
    }
    const skeleton = skeletonRef.current;
    skeletonRef.current = null;
    if (!enabled || !skeleton || !el || typeof el.animate !== "function") return;
    const ghost = skeletonGhost(skeleton.node, el, skeleton.height, withinOffsetParent);
    if (!ghost) return;
    const remove = () => ghost.remove();
    animate(
      ghost,
      { opacity: [1, 0] },
      { duration: seconds(SKELETON_FADE_MS), ease: EASE_OUT_SOFT_POINTS },
    ).finished.then(remove, remove);
    // Belt and braces: the copy never outlives its fade.
    window.setTimeout(remove, SKELETON_FADE_MS + 200);
  }, [loading, enabled, withinOffsetParent]);

  return {
    ref,
    contentClassName: !loading && sawLoadingRef.current ? "motion-content-in" : undefined,
  };
}

/**
 * A detached copy of the skeleton, laid over the content's slot on the
 * page (document coordinates, so it scrolls with the page) — or, with
 * `within`, inside the content's positioned ancestor (its coordinates, so
 * it scrolls and moves with it). `null` when the content has no box
 * (hidden, or not laid out).
 */
function skeletonGhost(
  skeleton: HTMLElement,
  content: HTMLElement,
  height: number,
  within = false,
): HTMLElement | null {
  const rect = content.getBoundingClientRect();
  if (rect.width === 0 || height === 0) return null;
  const host =
    within && content.offsetParent instanceof HTMLElement && content.offsetParent !== document.body
      ? content.offsetParent
      : null;
  const ghost = skeleton.cloneNode(true) as HTMLElement;
  for (const node of [ghost, ...Array.from(ghost.querySelectorAll<HTMLElement>("*"))]) {
    node.removeAttribute("data-testid");
    node.removeAttribute("id");
    // The copy's shimmer and fade-up must not restart: it leaves as it is.
    node.style.animation = "none";
  }
  ghost.setAttribute("aria-hidden", "true");
  ghost.setAttribute("data-skeleton-ghost", "");
  Object.assign(ghost.style, {
    position: "absolute",
    top: `${host ? content.offsetTop : rect.top + window.scrollY}px`,
    left: `${host ? content.offsetLeft : rect.left + window.scrollX}px`,
    width: `${host ? content.offsetWidth : rect.width}px`,
    height: `${height}px`,
    margin: "0",
    pointerEvents: "none",
    zIndex: "1",
    overflow: "hidden",
  });
  (host ?? document.body).appendChild(ghost);
  return ghost;
}
